import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FaEdit } from 'react-icons/fa';
import http from '../../Api/http';
import { encryptPassword } from '../../RsaEncryptor';
import UserDialog from './UserDialog';
import { useAuth } from '../AuthContext';
import { Navigate } from 'react-router-dom';
import { User } from 'lucide-react';

const Users = () => {
    const { t, i18n } = useTranslation();
    const { user, loadingUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currUser, setCurrUser] = useState(null);
    const isRtl = t('dir') === 'rtl';

    const openDialog = (userToEdit = null) => {
        setCurrUser(userToEdit ?? {
            ID: 0, Name: '', EName: '', UID: '', Password: '',
            Role: 1, Phone: '', Mobile: '',
            Email: '', StatusId: 1
        });
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setCurrUser(null);
    };

    const loadUsers = async () => {
        const lang = i18n.language || 'ar';
        const res = await http.getJsonAxios(http.actions.Users.GetUsers, { lang }, t);
        console.log(res)
        if (res.status === 200) setUsers(res.data);
        else alert("Error: " + res.message);
    };

    const saveUser = async (userData) => {
        try {
            if (userData.Password?.trim()) {
                userData.Password = encryptPassword(userData.Password);
            }
            setLoading(true);
            const result = await http.postJsonAxios(http.actions.Auth.SaveUser, userData, t);
            if (result.status === 200 && result.data.success) {
                await loadUsers();
                closeDialog();
            } else {
                alert(result.data.errorText || 'Save failed');
            }
        } catch (error) {
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, []);


    if (loadingUser) return null;
    if (!user || user.Role !== 1) return <Navigate to="/" />;

    return (
        <Box className="min-h-[70vh]" sx={{ p: 3, direction: isRtl ? 'rtl' : 'ltr' }}>
            <Box className="flex justify-between items-center mb-4">
                <h1 className="text-xl font-semibold">{t('users')}</h1>
                <button
                    onClick={() => openDialog()}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition focus:ring-2 focus:ring-blue-500 outline-none"
                >
                    {t('addUser')}
                </button>
            </Box>

            <div className="overflow-x-auto shadow rounded-lg">
                <table className="min-w-full divide-y divide-gray-300 border border-gray-300 text-sm md:table block w-full">
                    <thead className="bg-gray-100 md:table-header-group hidden">
                        <tr className="md:table-row block">
                            <th className="px-4 py-2 text-center border border-gray-300">{t('')}</th>
                            <th className="px-4 py-2 text-center border border-gray-300">{t('username')}</th>
                            <th className="px-4 py-2 text-center border border-gray-300">{t('email')}</th>
                            <th className="px-4 py-2 text-center border border-gray-300">{t('mobile')}</th>
                            <th className="px-4 py-2 text-center border border-gray-300">{t('role')}</th>
                            <th className="px-4 py-2 text-center border border-gray-300">{t('status')}</th>
                            <th className="px-4 py-2 text-center border border-gray-300">{t('onlinestatus')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 md:table-row-group block">
                        {users.length > 0 ? users.map((u, idx) => (
                            <tr key={idx} className="hover:bg-gray-50 md:table-row block border-b border-gray-200">
                                <td className="px-4 py-2 text-center border border-gray-300 md:table-cell block">
                                    <button
                                        onClick={() => openDialog(u)}
                                        className="text-blue-600 hover:text-blue-800 transition text-xl"
                                        title={t('edit')}
                                    >
                                        <FaEdit />
                                    </button>
                                </td>
                                <td className="px-4 py-2 border border-gray-300 md:table-cell block">{u.Name}</td>
                                <td className="px-4 py-2 border border-gray-300 md:table-cell block">{u.Email}</td>
                                <td className="px-4 py-2 border border-gray-300 md:table-cell block">{u.Mobile}</td>
                                <td className="px-4 py-2 border border-gray-300 md:table-cell block">{u.RoleName}</td>
                                <td className="px-4 py-2 border border-gray-300 md:table-cell block">{u.StatusName}</td>

                                <td className="px-4 py-2 border border-gray-300 md:table-cell block">
                                    {
                                        console.log(u)
                                    }
                                    {                                        
                                        u.IsOnline &&
                                        (
                                            <User className='text-green-500' />
                                        )
                                    }
                                    {
                                        !u.IsOnline &&
                                        (
                                            <User className='text-red-500' />
                                        )
                                    }
                                </td>
                            </tr>
                        )) : (
                            <tr className="block md:table-row">
                                <td colSpan={6} className="text-center py-4 text-gray-500 block">
                                    {t('noData')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>


            {dialogOpen && currUser && (
                <UserDialog
                    open={dialogOpen}
                    onClose={closeDialog}
                    onSave={saveUser}
                    user={currUser}
                    setUser={setCurrUser}
                    isRtl={isRtl}
                    loading={loading}
                />
            )}
        </Box>
    );
};

export default Users;
